// Copied from personalization/lib/stacked-lockup.js — keep in sync.
// personalization/lib/stacked-lockup.js
//
// ONE layout for a name + number lockup (both halves stacked in one placement
// box). The print renderer (personalization/index.js) runs it, and the
// storefront preview runs a byte-copy of it (shopify-theme/assets/
// gf-stacked-lockup.js: this file under a "Copied from" header line), so what a
// shopper previews and what prints are the same computation.
//
// Zero dependencies. Runs unmodified in Node (module.exports) and in the
// browser (window.GfStackedLockup).
(function (factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.GfStackedLockup = api;
})(function () {
  'use strict';

  // Grant 2026-08-13 lockup defaults. Must match shared/decoration-locations.js
  // DEFAULT_HEIGHT_SPLIT / DEFAULT_STACK_ORDER(_BACK): prize zones carry their
  // own stackOrder as platformConfig data and these are only its fallbacks.
  const DEFAULT_HEIGHT_SPLIT = Object.freeze({ number: 0.75, name: 0.25 });
  const DEFAULT_STACK_ORDER = Object.freeze(['number', 'name']);
  const DEFAULT_STACK_ORDER_BACK = Object.freeze(['name', 'number']);
  // Team stores print the classic jersey (Grant, 2026-09-24): name on top,
  // number below and bigger. The storefront preview has no per-product order,
  // so for team stores this is a rule, not data.
  const CLASSIC_JERSEY_ORDER = Object.freeze(['name', 'number']);
  // Per-store team lockups (teamStores.personalization.lockup; the theme reads
  // the collection metafield ghostfit.personalization_lockup). classic-jersey is
  // the default. number-over-name is the Pequot Lakes / Radford jacket look
  // (Grant, 2026-09-25): number on top at 75%, name under it at 25%, and a name
  // with no number prints at that same name-band height, not the full box.
  const DEFAULT_TEAM_STORE_LOCKUP = 'classic-jersey';
  const TEAM_STORE_LOCKUPS = Object.freeze({
    'classic-jersey': Object.freeze({ stackOrder: CLASSIC_JERSEY_ORDER, nameOnlyAtBand: false }),
    'number-over-name': Object.freeze({ stackOrder: Object.freeze(['number', 'name']), nameOnlyAtBand: true }),
  });
  const STACK_GAP_FRAC = 0.05; // internal gap, taken from the split so halves + gap = 100%
  // The preview works in fractions of its box, so jerseySliceBox lays the
  // lockup out at this height and divides back out. At 10000 the default split
  // divides evenly (gap 500, name 2375, number 7125), so the preview gets the
  // ideal fractions. Print rounds each edge to whole pixels at its own height,
  // so the two agree to within one print pixel.
  const SLICE_REFERENCE_HEIGHT = 10000;

  function defaultStackOrderForView(view) {
    return view === 'back' ? DEFAULT_STACK_ORDER_BACK.slice() : DEFAULT_STACK_ORDER.slice();
  }

  function resolveStackOrder(stackOrder, view) {
    if (Array.isArray(stackOrder) && stackOrder.length === 2
        && stackOrder.includes('name') && stackOrder.includes('number')) {
      return stackOrder.slice();
    }
    return defaultStackOrderForView(view);
  }

  function resolveHeightSplit(heightSplit) {
    if (heightSplit && Number.isFinite(heightSplit.number) && Number.isFinite(heightSplit.name)
        && heightSplit.number > 0 && heightSplit.name > 0) {
      return { number: heightSplit.number, name: heightSplit.name };
    }
    return { number: DEFAULT_HEIGHT_SPLIT.number, name: DEFAULT_HEIGHT_SPLIT.name };
  }

  function toHalfSet(presentHalves) {
    if (presentHalves instanceof Set) return presentHalves;
    return new Set(Array.isArray(presentHalves) ? presentHalves : []);
  }

  /**
   * Lay out a name+number lockup inside a zone of `height` px: integer px, at
   * least 4 (the renderer always passes whole pixels). Anything else throws.
   *
   * Percents stick to the HALF (number 75% / name 25%), not to top/bottom — an
   * inverted stack still gives the number 75%. A ~5% gap is taken from the
   * split so number + name + gap = 100%. A single present half uses the FULL
   * zone height (Grant 2026-08-12 decision 1). `presentHalves` is a Set or an
   * array of 'name' / 'number'. `stackOrder` must list each present half
   * exactly once (resolveStackOrder guarantees that for print); a stack order
   * that would drop or repeat a half throws rather than print the wrong lockup.
   */
  function layoutStackedLockup({ height, stackOrder, heightSplit, presentHalves }) {
    if (!Number.isInteger(height) || height < 4) {
      throw new Error(`layoutStackedLockup: height must be integer px, at least 4 (got ${height})`);
    }
    const present = toHalfSet(presentHalves);
    const split = resolveHeightSplit(heightSplit);
    const order = (Array.isArray(stackOrder) && stackOrder.length ? stackOrder : DEFAULT_STACK_ORDER)
      .filter((half) => present.has(half));
    for (const half of present) {
      if (half !== 'name' && half !== 'number') throw new Error(`layoutStackedLockup: unknown half ${half}`);
    }
    if (new Set(order).size !== order.length || order.length !== present.size) {
      throw new Error('layoutStackedLockup: stackOrder must list each present half exactly once');
    }
    if (!order.length) throw new Error('layoutStackedLockup: no present halves');
    if (order.length === 1) {
      const half = order[0];
      return {
        boxes: [{ half, top: 0, height }],
        gap: 0,
        numberH: half === 'number' ? height : 0,
        nameH: half === 'name' ? height : 0,
      };
    }
    const gap = Math.max(1, Math.round(height * STACK_GAP_FRAC));
    const usable = height - gap;
    const shareSum = split.number + split.name;
    // Clamped so an extreme split still leaves the name 1px and ends at `height`.
    const numberH = Math.min(usable - 1, Math.max(1, Math.round(usable * (split.number / shareSum))));
    const nameH = Math.max(1, usable - numberH);
    const heights = { number: numberH, name: nameH };
    const boxes = [];
    let y = 0;
    for (let i = 0; i < order.length; i += 1) {
      const half = order[i];
      boxes.push({ half, top: y, height: heights[half] });
      y += heights[half];
      if (i < order.length - 1) y += gap;
    }
    return { boxes, gap, numberH, nameH };
  }

  function resolveTeamStoreLockup(id) {
    const key = String(id == null ? '' : id).trim();
    return Object.prototype.hasOwnProperty.call(TEAM_STORE_LOCKUPS, key) ? key : DEFAULT_TEAM_STORE_LOCKUP;
  }

  function sliceFromLayout(half, present, stackOrder) {
    const layout = layoutStackedLockup({
      height: SLICE_REFERENCE_HEIGHT,
      stackOrder,
      heightSplit: DEFAULT_HEIGHT_SPLIT,
      presentHalves: present,
    });
    const slice = layout.boxes.find((b) => b.half === half);
    return { top: slice.top / SLICE_REFERENCE_HEIGHT, height: slice.height / SLICE_REFERENCE_HEIGHT };
  }

  /**
   * The storefront's slice of `box` for one half of a team-store lockup, for
   * the store's `lockupId` (unknown ids read as classic-jersey). Same contract
   * as jerseySliceBox. On number-over-name a name with no number keeps the
   * name-band height it has under a number and is centred in the box, which is
   * what the name-only print artwork is (production-spec nameOnlyAtStackedBand).
   */
  function teamStoreSliceBox(box, half, presentHalves, lockupId) {
    if (half !== 'name' && half !== 'number') return null;
    const present = new Set([...toHalfSet(presentHalves)].filter((h) => h === 'name' || h === 'number'));
    if (!present.has(half)) return null;
    const lockup = TEAM_STORE_LOCKUPS[resolveTeamStoreLockup(lockupId)];
    let frac;
    if (lockup.nameOnlyAtBand && half === 'name' && present.size === 1) {
      const band = sliceFromLayout('name', new Set(['name', 'number']), lockup.stackOrder);
      frac = { top: (1 - band.height) / 2, height: band.height };
    } else {
      frac = sliceFromLayout(half, present, lockup.stackOrder);
    }
    return { x: box.x, y: box.y + frac.top * box.h, w: box.w, h: frac.height * box.h };
  }

  /**
   * The storefront's slice of `box` for one half of a TEAM-STORE lockup: the
   * classic jersey at the renderer's split and gap. `box` is {x, y, w, h} in
   * any unit and the slice comes back in the same unit. Returns null when
   * `half` is not 'name' or 'number', or is not among `presentHalves` (nothing
   * of it prints, so nothing draws). A preview helper never throws on a stray
   * entry: anything in `presentHalves` but 'name' / 'number' is ignored here,
   * while print (layoutStackedLockup) stays strict.
   */
  function jerseySliceBox(box, half, presentHalves) {
    return teamStoreSliceBox(box, half, presentHalves, 'classic-jersey');
  }

  return {
    DEFAULT_HEIGHT_SPLIT,
    DEFAULT_STACK_ORDER,
    DEFAULT_STACK_ORDER_BACK,
    CLASSIC_JERSEY_ORDER,
    DEFAULT_TEAM_STORE_LOCKUP,
    TEAM_STORE_LOCKUPS,
    STACK_GAP_FRAC,
    SLICE_REFERENCE_HEIGHT,
    defaultStackOrderForView,
    resolveStackOrder,
    resolveHeightSplit,
    layoutStackedLockup,
    jerseySliceBox,
    resolveTeamStoreLockup,
    teamStoreSliceBox,
  };
});
